/**
 * Animal Health — shared UI primitives (v25.2 · Aug 2026)
 * =======================================================
 * Reused by both the Cohort workspace (`/animal-health/cohort`) and the
 * legacy Results page (`/animal-health/results`).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { InfoButton, HELP, CRITERION_HELP } from './animalHealthHelp';

export const TIER_META = {
  pursue: {
    id: 'pursue',
    label: 'Tier 1 – Pursue Now',
    short_label: 'Tier 1',
    range: '80–100',
    subtitle: 'Immediate detailed review, partner mapping and outreach',
    bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-900',
    chipBg: 'bg-emerald-100', chipBorder: 'border-emerald-300', chipText: 'text-emerald-800',
    dot: 'bg-emerald-500', fill: 'bg-emerald-500',
    activeBg: 'bg-emerald-600', activeText: 'text-white',
  },
  advance: {
    id: 'advance',
    label: 'Tier 2 – Advance / Monitor',
    short_label: 'Tier 2',
    range: '60–79',
    subtitle: 'Deeper AI review, expert assessment or additional evidence generation',
    bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-900',
    chipBg: 'bg-amber-100', chipBorder: 'border-amber-300', chipText: 'text-amber-900',
    dot: 'bg-amber-500', fill: 'bg-amber-500',
    activeBg: 'bg-amber-600', activeText: 'text-white',
  },
  pass: {
    id: 'pass',
    label: 'Tier 3 – Park / Pass',
    short_label: 'Tier 3',
    range: '0–59',
    subtitle: 'Not prioritised unless significant new evidence emerges',
    bg: 'bg-slate-50', border: 'border-slate-300', text: 'text-slate-800',
    chipBg: 'bg-slate-100', chipBorder: 'border-slate-300', chipText: 'text-slate-700',
    dot: 'bg-slate-400', fill: 'bg-slate-400',
    activeBg: 'bg-slate-600', activeText: 'text-white',
  },
};

export const TierChip = ({ recommendation, size = 'md', short = false }) => {
  const id = recommendation?.id || 'pass';
  const s = TIER_META[id] || TIER_META.pass;
  const sizeCls = size === 'sm' ? 'text-[10.5px] px-1.5 py-0.5' : 'text-[11.5px] px-2 py-1';
  const displayLabel = short ? s.short_label : (recommendation?.label || s.label);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border ${s.chipBg} ${s.chipText} ${s.chipBorder} ${sizeCls} font-semibold whitespace-nowrap`}
      data-testid={`ah-tier-chip-${id}`}
      title={s.label}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {displayLabel}
    </span>
  );
};

export const ConfidencePill = ({ value }) => {
  const v = String(value || 'Low');
  const cls = {
    High:   'bg-emerald-50 border-emerald-200 text-emerald-700',
    Medium: 'bg-amber-50 border-amber-200 text-amber-800',
    Low:    'bg-rose-50 border-rose-200 text-rose-700',
  }[v] || 'bg-slate-50 border-slate-200 text-slate-700';
  return (
    <span className={`inline-flex items-center rounded border px-1.5 text-[10px] font-semibold ${cls}`}>
      {v}
    </span>
  );
};

export const ScoreBar = ({ score, tierId }) => {
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const fill = TIER_META[tierId]?.fill || 'bg-slate-400';
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`${fill} h-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[13px] font-bold tabular-nums text-slate-800 w-10 text-right">
        {pct}
      </span>
    </div>
  );
};

export const AIStatusBadge = ({ status }) => {
  const map = {
    pending:   { label: 'Pending',   cls: 'bg-slate-50 border-slate-200 text-slate-600' },
    running:   { label: 'Running',   cls: 'bg-indigo-50 border-indigo-200 text-indigo-700 animate-pulse' },
    completed: { label: 'Completed', cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
    failed:    { label: 'Failed',    cls: 'bg-rose-50 border-rose-200 text-rose-700' },
  };
  const m = map[status] || map.pending;
  return (
    <span
      className={`inline-flex items-center rounded-full border text-[10.5px] px-2 py-0.5 font-semibold ${m.cls}`}
      data-testid={`ah-status-${status}`}
    >
      {m.label}
    </span>
  );
};

const MANUAL_DECISION_OPTIONS = [
  { id: 'pursue',       label: 'Pursue Now',        cls: 'border-emerald-300 hover:bg-emerald-50 text-emerald-800 data-[active=true]:bg-emerald-600 data-[active=true]:text-white' },
  { id: 'advance',      label: 'Advance / Monitor', cls: 'border-amber-300 hover:bg-amber-50 text-amber-800 data-[active=true]:bg-amber-600 data-[active=true]:text-white' },
  { id: 'pass',         label: 'Park / Pass',       cls: 'border-slate-300 hover:bg-slate-100 text-slate-700 data-[active=true]:bg-slate-700 data-[active=true]:text-white' },
  { id: 'not_reviewed', label: 'Not Yet Reviewed',  cls: 'border-slate-200 hover:bg-slate-50 text-slate-600 data-[active=true]:bg-slate-500 data-[active=true]:text-white' },
];

const _API = (process.env.REACT_APP_BACKEND_URL || '') + '/api/animal-health';

export const DeepDiveModal = ({ open, onClose, item, criteria = [], onManualDecisionSaved }) => {
  // Feb 2026 · Defensive default for `criteria` — when this modal is
  // opened from the Browse page (no cohort context) or on an asset
  // that hasn't been scored yet, no criteria prop is available. The
  // modal used to crash with `Cannot read properties of undefined
  // (reading 'map')` on `criteria.map` — coercing to [] keeps every
  // section rendering (empty rows just show a "—").
  criteria = Array.isArray(criteria) ? criteria : [];
  const [manual, setManual] = useState(item?.manual_decision?.id || 'not_reviewed');
  const [reason, setReason] = useState(item?.manual_decision?.reason || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setManual(item?.manual_decision?.id || 'not_reviewed');
    setReason(item?.manual_decision?.reason || '');
  }, [item?.asset_id, item?.manual_decision?.id, item?.manual_decision?.reason]);

  if (!item) return null;
  const critData = item.criteria || {};
  const aiDecisionId = item.recommendation?.id;
  const manualDiffers = manual !== 'not_reviewed' && aiDecisionId && manual !== aiDecisionId;

  const saveManualDecision = async () => {
    if (!item.asset_id) return;
    setSaving(true);
    try {
      const res = await axios.patch(
        `${_API}/assessment/asset/${item.asset_id}/manual-decision`,
        { decision: manual, reason: manualDiffers ? (reason || '').trim() : '' },
        { withCredentials: true },
      );
      toast.success('Manual decision saved');
      if (onManualDecisionSaved) onManualDecisionSaved(item.asset_id, res.data?.manual_decision);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save manual decision');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-y-auto"
        data-testid="ah-deep-dive-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="text-[18px] font-semibold text-slate-900 mb-0.5">
                {item.asset_name || 'Asset'}
              </div>
              <div className="text-[12px] text-slate-500 font-normal">
                {item.company_name || '—'}
                {item.country ? <span className="ml-1">· {item.country}</span> : null}
                {item.phase ? <span className="ml-1">· {item.phase}</span> : null}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center justify-end gap-1">
                  <span>AI Score</span>
                  <InfoButton {...HELP.aiScore} testId="info-modal-score" />
                </div>
                <div className="text-[24px] font-bold text-slate-900 tabular-nums leading-none">
                  {item.ai_opportunity_score ?? 0}
                  <span className="text-[13px] text-slate-400 font-medium">/100</span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="inline-flex items-center gap-1">
                  <TierChip recommendation={item.recommendation} />
                  <InfoButton {...HELP.decision} testId="info-modal-tier" />
                </span>
                {item.ai_confidence && (
                  <div className="flex items-center gap-1">
                    <span className="text-[9px] uppercase tracking-wide text-slate-500">AI conf</span>
                    <ConfidencePill value={item.ai_confidence} />
                    <InfoButton {...HELP.aiConfidence} testId="info-modal-confidence" />
                  </div>
                )}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          {[
            ['Human primary indication',   item.primary_indication,           HELP.humanPrimaryIndication],
            ['Human secondary indication', item.secondary_indication,         HELP.humanSecondaryIndication],
            ['Human therapeutic area',     item.therapeutic_area,             HELP.humanTherapeuticArea],
            ['Development stage',          item.phase,                        HELP.developmentStage],
            ['Matched AH TA',              item.matched_ah_therapeutic_area,  HELP.matchedAhTa],
            ['Matched AH disease',         item.matched_ah_disease_area,      HELP.matchedAhDisease],
            ['Species relevance',          item.species_relevance,            HELP.speciesRelevance],
            ['Modality',                   item.modality,                     HELP.modality],
          ].map(([k, v, help]) => (
            <div key={k} className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
              <div className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1">
                <span>{k}</span>
                {help && <InfoButton {...help} />}
              </div>
              <div className="text-[11.5px] text-slate-800 truncate" title={v || ''}>{v || '—'}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {criteria.map(c => {
            const cc = critData[c.key] || {};
            const critHelp = CRITERION_HELP[c.key];
            return (
              <div key={c.key} className="bg-white border border-slate-200 rounded-md p-3" data-testid={`ah-dd-crit-${c.key}`}>
                <div className="flex items-start justify-between gap-3 mb-1.5">
                  <div className="text-[12px] font-semibold text-slate-800 flex-1 flex items-center gap-1">
                    <span>{c.label}</span>
                    {critHelp && <InfoButton {...critHelp} testId={`info-crit-${c.key}`} />}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[13px] font-bold text-slate-900 tabular-nums">
                      {cc.score ?? 0}<span className="text-slate-400 text-[10px] font-medium">/10</span>
                    </span>
                    <span className="inline-flex items-center gap-0.5">
                      <ConfidencePill value={cc.confidence} />
                      <InfoButton {...HELP.criterionConfidence} testId={`info-crit-conf-${c.key}`} />
                    </span>
                  </div>
                </div>
                <p className="text-[11.5px] text-slate-600 leading-snug mb-1.5">
                  {cc.rationale || <em className="text-slate-400">No rationale returned.</em>}
                </p>
                {(cc.key_evidence || []).length > 0 && (
                  <>
                    <div className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1 mb-0.5">
                      <span>Supporting evidence</span>
                      <InfoButton {...HELP.criterionEvidence} testId={`info-crit-evidence-${c.key}`} />
                    </div>
                    <ul className="text-[11px] text-slate-500 space-y-0.5 pl-3 list-disc">
                      {(cc.key_evidence || []).map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </>
                )}
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
            <div className="text-[10.5px] uppercase tracking-wide text-emerald-800 font-semibold mb-1 flex items-center gap-1">
              <span>Key strengths</span>
              <InfoButton {...HELP.keyStrengths} testId="info-key-strengths" />
            </div>
            {(item.key_strengths || []).length ? (
              <ul className="text-[11.5px] text-emerald-900 space-y-1 pl-3 list-disc">
                {(item.key_strengths || []).map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            ) : <em className="text-[11px] text-emerald-700">—</em>}
          </div>
          <div className="bg-rose-50 border border-rose-200 rounded-md p-3">
            <div className="text-[10.5px] uppercase tracking-wide text-rose-800 font-semibold mb-1 flex items-center gap-1">
              <span>Key risks / evidence gaps</span>
              <InfoButton {...HELP.keyRisks} testId="info-key-risks" />
            </div>
            {(item.key_risks || []).length ? (
              <ul className="text-[11.5px] text-rose-900 space-y-1 pl-3 list-disc">
                {(item.key_risks || []).map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            ) : <em className="text-[11px] text-rose-700">—</em>}
          </div>
          <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3">
            <div className="text-[10.5px] uppercase tracking-wide text-indigo-800 font-semibold mb-1 flex items-center gap-1">
              <span>Recommended next action</span>
              <span className="text-[9px] font-normal text-indigo-500">· AI</span>
              <InfoButton {...HELP.recommendedNextAction} testId="info-next-action" />
            </div>
            <p className="text-[11.5px] text-indigo-900">
              {item.recommended_next_action || <em className="text-indigo-700">—</em>}
            </p>
          </div>
        </div>

        {/* Animal Data section (Feb 2026) — public + client-manual
            evidence, separate from the AI opportunity score. */}
        <AnimalDataSection assetId={item.asset_id} aiScore={item.ai_opportunity_score} />

        {/* Manual decision panel — never overwrites the AI recommendation */}
        <div className="mt-4 border border-slate-200 rounded-md p-3 bg-slate-50" data-testid="ah-dd-manual">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-[12px] font-semibold text-slate-800 flex items-center gap-1">
                <span>Committee / manual decision</span>
                <InfoButton {...HELP.manualDecision} testId="info-manual-decision" />
              </div>
              <p className="text-[10.5px] text-slate-500">Recorded separately from the AI recommendation.</p>
            </div>
            {item.manual_decision?.set_at && (
              <div className="text-[10px] text-slate-500 text-right">
                Last saved by <span className="font-medium">{item.manual_decision.set_by || '—'}</span><br />
                {new Date(item.manual_decision.set_at).toLocaleString()}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {MANUAL_DECISION_OPTIONS.map(opt => (
              <button
                key={opt.id}
                type="button"
                data-active={manual === opt.id}
                onClick={() => setManual(opt.id)}
                className={`text-[11px] font-medium px-2.5 py-1 rounded border transition ${opt.cls}`}
                data-testid={`ah-manual-${opt.id}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {manualDiffers && (
            <div className="mb-2">
              <div className="text-[10.5px] text-amber-800 mb-1">
                Manual decision differs from AI recommendation — capture the reason (optional but recommended):
              </div>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why does the committee disagree with the AI recommendation?"
                className="text-[11.5px]"
                rows={2}
                data-testid="ah-manual-reason"
              />
            </div>
          )}
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={saveManualDecision}
              disabled={saving}
              className="h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="ah-manual-save"
            >
              {saving ? 'Saving…' : 'Save manual decision'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ────────────────────────────────────────────────────────────────────
//  Animal Data — status pill + Deep Dive section  (Feb 2026)
// ────────────────────────────────────────────────────────────────────
const _ANIMAL_STATUS_META = {
  'available':      { label: 'Animal data available',    cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  'manual_added':   { label: 'Manual evidence added',    cls: 'bg-blue-100    text-blue-800    border-blue-300' },
  'not_identified': { label: 'No animal data identified',cls: 'bg-slate-100   text-slate-700   border-slate-300' },
  'not_reviewed':   { label: 'Not yet reviewed',         cls: 'bg-amber-50    text-amber-800   border-amber-200' },
};

export const AnimalDataPill = ({ status }) => {
  const meta = _ANIMAL_STATUS_META[status] || _ANIMAL_STATUS_META.not_reviewed;
  return (
    <span
      className={`inline-flex items-center text-[10.5px] font-medium px-1.5 py-0.5 rounded border whitespace-nowrap ${meta.cls}`}
      data-testid={`animal-data-pill-${status}`}
    >
      {meta.label}
    </span>
  );
};


export const AnimalDataSection = ({ assetId, aiScore }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    source: 'manual', study_summary: '', species: '', disease: '',
    comparator: '', key_outcome: '', source_reference: '', evidence_score: 5,
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!assetId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${_API}/asset/${assetId}/animal-data`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      // 404s and permission errors are non-fatal — Section stays empty
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [assetId]);

  const saveEntry = async () => {
    if (!form.study_summary.trim()) { toast.error('Study summary is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${_API}/asset/${assetId}/animal-data`, form, { withCredentials: true });
      toast.success('Animal data entry saved');
      setForm({ source: 'manual', study_summary: '', species: '', disease: '', comparator: '', key_outcome: '', source_reference: '', evidence_score: 5 });
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Delete this evidence entry?')) return;
    try {
      await axios.delete(`${_API}/animal-data/${id}`, { withCredentials: true });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const publicRows = data?.public_entries || [];
  const manualRows = data?.manual_entries || [];
  const scoring = data?.scoring || {};
  const combined = (Number(aiScore) || 0) + (Number(scoring.total_bonus) || 0);

  return (
    <div className="mt-4 border border-teal-200 rounded-md p-3 bg-teal-50/60" data-testid="ah-dd-animal-data">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="text-[12px] font-semibold text-teal-900 flex items-center gap-2">
            <span>Animal Data</span>
            {loading ? (
              <span className="text-[10.5px] text-teal-700">loading…</span>
            ) : (
              <AnimalDataPill status={data?.status} />
            )}
          </div>
          <p className="text-[10.5px] text-teal-800 mt-0.5">
            Public and manually-entered evidence about this asset's performance in animal models.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowForm(v => !v)}
          className="h-7 text-[11px] border-teal-300 text-teal-800 hover:bg-teal-100"
          data-testid="ah-animal-add-btn"
        >
          {showForm ? 'Cancel' : '+ Add entry'}
        </Button>
      </div>

      {/* Scoring breakdown — 3 lines per Amit's spec */}
      {!loading && data && (
        <div className="mb-3 grid grid-cols-4 gap-2 text-[11px]" data-testid="ah-animal-scoring">
          <ScoreLine label="AI base score"          value={aiScore || 0}                unit="/100" />
          <ScoreLine label="Public evidence bonus"  value={scoring.public_bonus || 0}   unit="pts"  tone="blue" />
          <ScoreLine label="Manual evidence bonus"  value={scoring.manual_bonus || 0}   unit="pts"  tone="indigo" />
          <ScoreLine label="Combined score"         value={combined}                    unit="/115" tone="emerald" bold />
        </div>
      )}

      {/* Add-entry form */}
      {showForm && (
        <div className="mb-3 p-2.5 bg-white border rounded space-y-2" data-testid="ah-animal-form">
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Source">
              <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
                      className="h-7 w-full text-[11.5px] border rounded px-2" data-testid="ah-animal-form-source">
                <option value="manual">Manual (client / BD)</option>
                <option value="public">Public (paper / press / registry)</option>
              </select>
            </FormField>
            <FormField label="Evidence score (0-10)">
              <input type="number" min="0" max="10" value={form.evidence_score}
                     onChange={e => setForm(f => ({ ...f, evidence_score: e.target.value }))}
                     className="h-7 w-full text-[11.5px] border rounded px-2" data-testid="ah-animal-form-score" />
            </FormField>
          </div>
          <FormField label="Study / evidence summary *">
            <Textarea rows={2} value={form.study_summary} placeholder="e.g. Ex vivo canine keratinocyte assay showed 82% JAK1 inhibition."
                      onChange={e => setForm(f => ({ ...f, study_summary: e.target.value }))}
                      className="text-[11.5px]" data-testid="ah-animal-form-summary" />
          </FormField>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Species">
              <input value={form.species} placeholder="Canine, Feline, Bovine…"
                     onChange={e => setForm(f => ({ ...f, species: e.target.value }))}
                     className="h-7 w-full text-[11.5px] border rounded px-2" data-testid="ah-animal-form-species" />
            </FormField>
            <FormField label="Disease / indication">
              <input value={form.disease} placeholder="Atopic dermatitis"
                     onChange={e => setForm(f => ({ ...f, disease: e.target.value }))}
                     className="h-7 w-full text-[11.5px] border rounded px-2" data-testid="ah-animal-form-disease" />
            </FormField>
            <FormField label="Comparator">
              <input value={form.comparator} placeholder="Apoquel, vehicle control…"
                     onChange={e => setForm(f => ({ ...f, comparator: e.target.value }))}
                     className="h-7 w-full text-[11.5px] border rounded px-2" data-testid="ah-animal-form-comparator" />
            </FormField>
            <FormField label="Key outcome">
              <input value={form.key_outcome} placeholder="82% JAK1 inhibition"
                     onChange={e => setForm(f => ({ ...f, key_outcome: e.target.value }))}
                     className="h-7 w-full text-[11.5px] border rounded px-2" data-testid="ah-animal-form-outcome" />
            </FormField>
          </div>
          <FormField label="Source URL / reference">
            <input value={form.source_reference} placeholder="pubmed link, PDF filename, or 'BD conversation 2026-02-14'"
                   onChange={e => setForm(f => ({ ...f, source_reference: e.target.value }))}
                   className="h-7 w-full text-[11.5px] border rounded px-2" data-testid="ah-animal-form-reference" />
          </FormField>
          <div className="flex justify-end">
            <Button size="sm" onClick={saveEntry} disabled={saving}
                    className="h-7 text-[11px] bg-teal-600 hover:bg-teal-700 text-white"
                    data-testid="ah-animal-form-save">
              {saving ? 'Saving…' : 'Save entry'}
            </Button>
          </div>
        </div>
      )}

      {/* Two lists — public + manual, per Amit's spec */}
      {!loading && (
        <div className="grid md:grid-cols-2 gap-2" data-testid="ah-animal-lists">
          <EntryList title="Public evidence" tone="blue" rows={publicRows} onDelete={deleteEntry} />
          <EntryList title="Manual evidence" tone="indigo" rows={manualRows} onDelete={deleteEntry} />
        </div>
      )}
    </div>
  );
};


function ScoreLine({ label, value, unit, tone = 'slate', bold = false }) {
  const toneMap = {
    slate:   'bg-slate-50 border-slate-200 text-slate-800',
    blue:    'bg-blue-50 border-blue-200 text-blue-900',
    indigo:  'bg-indigo-50 border-indigo-200 text-indigo-900',
    emerald: 'bg-emerald-50 border-emerald-300 text-emerald-900',
  };
  return (
    <div className={`px-2 py-1.5 rounded border ${toneMap[tone]}`}>
      <div className="text-[9px] uppercase tracking-wide opacity-70">{label}</div>
      <div className={`tabular-nums ${bold ? 'text-[16px] font-bold' : 'text-[14px] font-semibold'}`}>
        {value}<span className="text-[10px] font-normal opacity-60 ml-0.5">{unit}</span>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">{label}</div>
      {children}
    </label>
  );
}

function EntryList({ title, tone, rows, onDelete }) {
  const toneMap = {
    blue:   'border-blue-200 bg-white',
    indigo: 'border-indigo-200 bg-white',
  };
  return (
    <div className={`rounded border ${toneMap[tone]} p-2`}>
      <div className="text-[10.5px] font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
        {title} <span className="text-slate-400">({rows.length})</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-[11px] text-slate-400 italic p-1">No entries yet.</div>
      ) : (
        <ul className="space-y-1.5">
          {rows.map(r => (
            <li key={r.id} className="text-[11.5px] p-2 rounded bg-slate-50 border border-slate-200">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="text-slate-800">{r.study_summary}</div>
                  <div className="text-[10.5px] text-slate-500 mt-0.5 space-x-1.5">
                    {r.species && <span>· {r.species}</span>}
                    {r.disease && <span>· {r.disease}</span>}
                    {r.comparator && <span>· vs {r.comparator}</span>}
                    {r.key_outcome && <span>· {r.key_outcome}</span>}
                    <span>· score {r.evidence_score}</span>
                  </div>
                  {r.source_reference && (
                    <div className="text-[10.5px] text-slate-500 mt-0.5">
                      <span className="font-medium">Source:</span>{' '}
                      {r.source_reference.startsWith('http') ? (
                        <a href={r.source_reference} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{r.source_reference}</a>
                      ) : r.source_reference}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => onDelete(r.id)}
                  className="text-[10.5px] text-rose-600 hover:text-rose-800 opacity-60 hover:opacity-100"
                  data-testid={`ah-animal-delete-${r.id}`}
                >×</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
